import { useEffect } from 'react'
import mapboxgl from 'mapbox-gl'
// import { ZoningType } from '@popety-io/types'
import {
  ZONING_TYPES,
  getZoningSourceId,
  getZoningLayerId,
  getZoningOutlineLayerId,
  getZoningLayerStyle,
} from './zoningLayerHelpers'
import { zonesToGeoJSON } from '../../../../molecules/AnalysisMap/utils/mapDataTransformers'
import { getLayerConfig } from '../../../../molecules/AnalysisMap/config/dynamicLayersConfig'
import type { ZoningDataByType } from '../../../ZoningSection/utils/zoningHelpers'

// interface UseZoningVectorLayersProps {
//   map: React.MutableRefObject<mapboxgl.Map | null>
//   mapLoaded: boolean
//   popup: React.MutableRefObject<mapboxgl.Popup | null>
//   styleLoadedCount: number
//   otherMapOptions: any
// }

/**
 * Hook to handle zoning GeoJSON layers with type filtering
 *
 * Creates 4 GeoJSON sources (one per type) from batch API data
 * with 8 layers total (4 fill + 4 outline).
 * Manages visibility based on selectedZoningType and disabledZoningTypes from context.
 */
export const useZoningVectorLayers = (
  map: React.MutableRefObject<mapboxgl.Map | null>,
  mapLoaded: boolean,
  styleLoadedCount: number,
  otherMapOptions: any
) => {
  // Get zoning-specific options from otherMapOptions
  const activeSection = (otherMapOptions as any).activeSection
  const zoningSection =
    activeSection === 'zoning' || activeSection === 'zoning_data'
  const zoningData = (otherMapOptions as any).sectionData?.zoningData as
    | ZoningDataByType
    | undefined
  const selectedZoningType =
    (otherMapOptions as any).selectedZoningType || 'all'
  const disabledZoningTypes = (otherMapOptions as any).disabledZoningTypes || []
  const isSatelliteZoning = (otherMapOptions as any).isSatelliteZoning || false
  const layerStates = (otherMapOptions as any).demographicsLayerStates // Get layer states for opacity

  // Effect to add/update GeoJSON-based zoning layers
  useEffect(() => {
    if (!map.current || !mapLoaded || !zoningSection) {
      return
    }

    // Check if style is fully loaded before adding sources/layers
    if (!map.current.isStyleLoaded()) {
      return
    }

    if (!zoningData || Object.keys(zoningData).length === 0) {
      return
    }

    // Get the active zoning layer config to determine opacity
    // The active layer depends on the selected filter
    const getActiveZoningLayerId = () => {
      if (selectedZoningType === 'all') {
        return 'zoning-all'
      } else {
        return `zoning-${selectedZoningType}-only`
      }
    }

    const activeZoningLayerId = getActiveZoningLayerId()
    const layerConfig = getLayerConfig(activeZoningLayerId)
    const layerState = layerStates?.[activeZoningLayerId] || {
      enabled: layerConfig?.defaultEnabled !== false,
      opacity: layerConfig?.defaultOpacity || 30,
    }

    // Iterate through each zoning type and create/update sources and layers
    ZONING_TYPES.forEach((type) => {
      const sourceId = getZoningSourceId(type)
      const layerId = getZoningLayerId(type)
      const outlineLayerId = getZoningOutlineLayerId(type)
      const colors = getZoningLayerStyle(type, isSatelliteZoning)

      // Determine visibility
      const isTypeDisabled = disabledZoningTypes.includes(type)
      const isVisible =
        !isTypeDisabled &&
        (selectedZoningType === 'all' || selectedZoningType === type)

      // Get zones for this type and convert to GeoJSON
      const zones = zoningData[type] || []
      const geojsonData = zonesToGeoJSON(zones)

      // Create or update GeoJSON source
      const existingSource = map.current!.getSource(
        sourceId
      ) as mapboxgl.GeoJSONSource
      if (existingSource) {
        existingSource.setData(geojsonData)
      } else {
        map.current!.addSource(sourceId, {
          type: 'geojson',
          data: geojsonData,
        })
      }

      // Calculate opacity (convert from 0-100 to 0-1)
      const fillOpacity = layerState.opacity / 100

      // Add fill layer if it doesn't exist
      if (!map.current!.getLayer(layerId)) {
        map.current!.addLayer({
          id: layerId,
          type: 'fill',
          source: sourceId,
          layout: {
            visibility: isVisible ? 'visible' : 'none',
          },
          paint: {
            'fill-color': colors.fill,
            'fill-opacity': fillOpacity,
          },
        })
      } else {
        // Update existing layer visibility, colors, and opacity
        map.current!.setLayoutProperty(
          layerId,
          'visibility',
          isVisible ? 'visible' : 'none'
        )
        map.current!.setPaintProperty(layerId, 'fill-color', colors.fill)
        map.current!.setPaintProperty(layerId, 'fill-opacity', fillOpacity)
      }

      // Add outline layer if it doesn't exist
      if (!map.current!.getLayer(outlineLayerId)) {
        map.current!.addLayer({
          id: outlineLayerId,
          type: 'line',
          source: sourceId,
          layout: {
            visibility: isVisible ? 'visible' : 'none',
          },
          paint: {
            'line-color': colors.outline,
            'line-width': 2,
          },
        })
      } else {
        // Update existing outline layer visibility and colors
        map.current!.setLayoutProperty(
          outlineLayerId,
          'visibility',
          isVisible ? 'visible' : 'none'
        )
        map.current!.setPaintProperty(
          outlineLayerId,
          'line-color',
          colors.outline
        )
      }
    })
  }, [mapLoaded, styleLoadedCount, otherMapOptions])

  // Hover tooltip effect for zoning layers
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    // Check if style is fully loaded before querying layers
    if (!map.current.isStyleLoaded()) return

    const zoningSection = (otherMapOptions as any).activeSection === 'zoning'
    if (!zoningSection) return

    // Create popup for tooltips
    const popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      className: 'zoning-popup',
    })

    // Handle mouse movement over zoning layers
    const handleMouseMove = (e: mapboxgl.MapMouseEvent) => {
      if (!map.current) return

      // Query all zoning layers
      const features = map.current.queryRenderedFeatures(e.point, {
        layers: ZONING_TYPES.map((type) => getZoningLayerId(type)),
      })

      if (features.length > 0) {
        const feature = features[0]
        const props = feature.properties

        if (props && props.zone_name) {
          // Show popup with zone info
          const html = `
            <div style="padding: 8px;">
              <strong>${props.zone_name}</strong>
              ${props.canton ? `<br/>Canton: ${props.canton}` : ''}
              ${props.municipality ? `<br/>Municipality: ${props.municipality}` : ''}
            </div>
          `

          popup.setLngLat(e.lngLat).setHTML(html).addTo(map.current)
          map.current.getCanvas().style.cursor = 'pointer'
        }
      } else {
        popup.remove()
        map.current.getCanvas().style.cursor = ''
      }
    }

    // Handle mouse leaving the map
    const handleMouseLeave = () => {
      popup.remove()
      if (map.current) {
        map.current.getCanvas().style.cursor = ''
      }
    }

    // Add event listeners
    map.current.on('mousemove', handleMouseMove)
    map.current.on('mouseleave', handleMouseLeave)

    // Cleanup
    return () => {
      if (map.current) {
        map.current.off('mousemove', handleMouseMove)
        map.current.off('mouseleave', handleMouseLeave)
      }
      popup.remove()
    }
  }, [mapLoaded, otherMapOptions])
}
